import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { z } from "zod";
import { http } from "../../../utils/httpCommon";
import { useApiUrls } from "../../useApiUrl";
import type { TripHistoryResponse } from "./useTripHistory";

dayjs.extend(utc);

const ucccOfdLocationDetailsSchema = z.object({
  state: z.string().nullish(),
  city: z.string().nullish(),
  zipCode: z.string().nullish(),
  locationSic: z.string().nullable(),
  timezone: z.string().nullish(),
  timezoneOffset: z.string().nullish(),
});

const ucccOfdStatusHistoryItemSchema = z.object({
  id: z.number(),
  currentSic: z.string().nullable(),
  status: z.string(),
  locationId: z.number().nullable(),
  locationDetails: ucccOfdLocationDetailsSchema,
  statusStartDateUtc: z.union([z.string(), z.date()]),
  statusEndDateUtc: z.union([z.string(), z.date()]).nullable(),
  createdById: z.string().nullable(),
  createdBy: z.string(),
  createdOn: z.union([z.string(), z.date()]).nullable(),
  modifiedById: z.string().nullable(),
  modifiedBy: z.string().nullish(),
  modifiedOn: z.union([z.string(), z.date()]).nullable(),
  reasonForDelay: z.string().nullable(),
  reportedIssue: z.string().nullable(),
  isServiceFailure: z.boolean(),
});

export const ucccOfdShipmentStatusHistoryResponse = z.object({
  shipmentId: z.number(),
  statusHistory: z.array(ucccOfdStatusHistoryItemSchema),
});

const ucccOfdShipmentHistoryKeys = {
  all: ["linehaul", "uccc-ofd", "shipment-status-history"] as const,
  shipmentHistory: (shipmentId: number | string) =>
    [...ucccOfdShipmentHistoryKeys.all, shipmentId] as const,
};

function toDayjsUtc(value: string | Date | null | undefined) {
  if (value === null || value === undefined) {
    return null;
  }
  return dayjs.utc(value);
}

/**
 * Maps UC/CC OFD shipment status-history API payload to the shape expected by TripHistoryTimeline.
 */
function mapUcccOfdShipmentStatusHistoryToTripHistoryResponse(
  data: z.infer<typeof ucccOfdShipmentStatusHistoryResponse>
): TripHistoryResponse {
  const firstTz =
    data.statusHistory[0]?.locationDetails?.timezone?.trim() ??
    "America/New_York";

  const placeholderSic = {
    id: 0,
    code: "",
    ownership: "",
    lhRegionId: null as number | null,
    timeZone: firstTz,
  };

  const statusTripHistory = data.statusHistory.map((item) => ({
    id: item.id,
    tripStatus: item.status,
    locationDetails: {
      state: item.locationDetails.state ?? null,
      city: item.locationDetails.city ?? null,
      zipCode: item.locationDetails.zipCode ?? null,
      locationSic: item.locationDetails.locationSic,
      timezoneOffset: item.locationDetails.timezoneOffset,
      timezone: item.locationDetails.timezone,
    },
    createdBy: item.createdBy,
    createdOn: toDayjsUtc(item.createdOn),
    createdById: item.createdById,
    modifiedBy: item.modifiedBy ?? "",
    modifiedOn: toDayjsUtc(item.modifiedOn),
    statusUpdateTime: dayjs.utc(item.statusStartDateUtc),
    statusEndDateUtc: toDayjsUtc(item.statusEndDateUtc),
    sameCity: false,
    reason: item.reasonForDelay,
    reasonComment: item.reportedIssue,
    isServiceFailure: item.isServiceFailure,
  }));

  return {
    originSic: placeholderSic,
    destinationSic: placeholderSic,
    deadheadOriginSic: "",
    deadheadDestinationSic: "",
    stops: [],
    statusTripHistory,
  };
}

type UseUcccOfdShipmentHistoryParams = {
  shipmentId: number | string | null;
  enabled?: boolean;
  sortBy?: string;
  sortOrder?: "Ascending" | "Descending";
};

export function useUcccOfdShipmentHistory({
  shipmentId,
  enabled = true,
  sortBy,
  sortOrder,
}: UseUcccOfdShipmentHistoryParams) {
  const urls = useApiUrls();

  return useQuery({
    enabled: enabled && Boolean(shipmentId),
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- urls is stable during session
    queryKey: [
      ...ucccOfdShipmentHistoryKeys.shipmentHistory(shipmentId ?? ""),
      sortBy,
      sortOrder,
    ],
    queryFn: async () => {
      if (shipmentId === null) {
        throw new Error("shipmentId is required");
      }
      const { data } = await http.get(
        urls.ucccOfd.getUcccOfdShipmentStatusHistory(shipmentId),
        {
          params: {
            ...(sortBy && { SortBy: sortBy }),
            ...(sortOrder && { SortOrder: sortOrder }),
          },
        }
      );
      const parsed = ucccOfdShipmentStatusHistoryResponse.parse(data);
      return mapUcccOfdShipmentStatusHistoryToTripHistoryResponse(parsed);
    },
  });
}
